/**
 * Session Context for SSR
 *
 * This module provides a way to access the session ID during server-side rendering.
 * On the server, AsyncLocalStorage (set up in workers/app.ts and exposed via globalThis)
 * isolates state per-request in Cloudflare Workers, preventing session leakage.
 * On the client, simple module-level variables are used (single user, no concurrency issue).
 */
import * as cookie from "cookie";

import { setRequestContext } from "./requestContext";

const SESSION_COOKIE_NAME = "X-Session-Id";
const SESSION_MAX_AGE = 60 * 60 * 24 * 30; // 30 days

// Access the AsyncLocalStorage instance created in workers/app.ts.
// Returns undefined on the client (no ALS available).
function getServerStore(): { sessionId: string | null } | undefined {
	return (globalThis as any).__sessionALS?.getStore() ?? undefined;
}

export function setServerSessionId(sessionId: string | null) {
	const store = getServerStore();
	if (store) {
		store.sessionId = sessionId;
	}
}

export function getServerSessionId(): string | null {
	return getServerStore()?.sessionId ?? null;
}

export function clearServerSessionId() {
	const store = getServerStore();
	if (store) {
		store.sessionId = null;
	}
}

// Client-side session ID storage (for when cookies aren't accessible)
let clientSessionId: string | null = null;

export function setClientSessionId(sessionId: string | null) {
	clientSessionId = sessionId;
	// Also store in a cookie for cross-tab persistence
	if (typeof document !== "undefined" && sessionId) {
		document.cookie = cookie.serialize(SESSION_COOKIE_NAME, sessionId, {
			maxAge: SESSION_MAX_AGE,
			path: "/",
			sameSite: "lax",
		});
	}
}

export function getClientSessionId(): string | null {
	if (clientSessionId) return clientSessionId;

	// Try to read from cookie
	if (typeof document !== "undefined") {
		const cookies = cookie.parse(document.cookie);
		if (cookies[SESSION_COOKIE_NAME]) {
			clientSessionId = cookies[SESSION_COOKIE_NAME];
			return clientSessionId;
		}
	}
	return null;
}

/**
 * Get the session ID for the current environment
 * Works on both server (SSR) and client
 */
export function getSessionId(): string | null {
	const isSsr = typeof window === "undefined";
	return isSsr ? getServerSessionId() : getClientSessionId();
}

/**
 * Initialize server session from a Request object.
 * Call this at the start of any server loader that makes API calls.
 * This ensures the session is available even for data requests during client navigation.
 * Also sets up the request context for proper API URL resolution.
 */
export function initServerSession(request: Request): string | null {
	// Set request context for Config to resolve the correct API URL
	setRequestContext(request);

	const cookieHeader = request.headers.get("Cookie");
	if (cookieHeader) {
		const cookies = cookie.parse(cookieHeader);
		const sessionId = cookies[SESSION_COOKIE_NAME] || null;
		if (sessionId) {
			setServerSessionId(sessionId);
		}
		return sessionId;
	}
	return null;
}

/**
 * Clear the client-side session (memory and cookie)
 */
export function clearClientSession() {
	clientSessionId = null;
	// Clear the cookie by setting it to expire immediately
	if (typeof document !== "undefined") {
		document.cookie = cookie.serialize(SESSION_COOKIE_NAME, "", {
			maxAge: 0,
			path: "/",
			sameSite: "lax",
		});
	}
}
